import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, HostBinding, Input, OnDestroy, Optional, Output, Self, EventEmitter } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroupDirective, NgControl, Validators } from '@angular/forms';
import { MatLegacyFormField as MatFormField, MatLegacyFormFieldControl as MatFormFieldControl } from '@angular/material/legacy-form-field';
import { Subject, BehaviorSubject, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'bw-autocomplete',
  templateUrl: './bw-autocomplete.component.html',
  styleUrls: ['./bw-autocomplete.component.scss'],
  providers: [
    { provide: MatFormFieldControl, useExisting: BwAutocompleteComponent }
  ]
})
export class BwAutocompleteComponent 
  implements MatFormFieldControl<any>, ControlValueAccessor, OnDestroy {
  //#region Inherit property
  static nextId = 0;
  @HostBinding() id = `bw-autocomplete-input-${BwAutocompleteComponent.nextId++}`;
  stateChanges = new Subject<void>();
  statusChanges = new Subject<void>();
  ngControl: NgControl | null = null;
  focused = false;
  touched = false;
  get empty() {
    return !this.value;
  }
  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }
  @Input()
  get required() {
    return this._required;
  }
  set required(req: any) {
    this._required = coerceBooleanProperty(req);
    this._required ? this.control?.setValidators(Validators.required) : this.control?.setValidators([]);
    this.stateChanges.next();
    this.statusChanges.next();
  }
  private _required = false;
  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.control?.disable() : this.control?.enable();
    this.stateChanges.next();
    this.statusChanges.next();
  }
  private _disabled = false;
  get errorState(): boolean {
    return this.control?.invalid && this.touched;
  }
  controlType = 'bw-autocomplete';
  autofilled?: boolean;
  describedBy: string = '';
  // tslint:disable-next-line: no-input-rename
  @Input('aria-describedby') userAriaDescribedBy: string = '';

  private _value: string | undefined;
  set value(id: any | null) {
    this._value = id;
    this.control?.setValue(id, { emitEvent: false });
    this.stateChanges.next();
    this.statusChanges.next();
  }
  get value() {
    return this._value;
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
    this.statusChanges.next();
  }
  private _placeholder: string = '';
  //#endregion

  @Input() displayMember: string | ((item: any) => string) = 'string';
  @Input() secondLineMember?: string | ((item: any) => string);
  @Input() valueMember?: string = 'id';
  @Input()
  get source(): any {
    return this._source;
  }
  set source(val: any) {
    this._source = val;
    this.downloadOrigin();
  }
  @Output() optionSelectedEvent = new EventEmitter();

  private _source: any;

  sourceSubs: Subscription | undefined;
  valueChangeSubs: Subscription;
  control = new FormControl();
  origin: any[] = [];
  filtered: any[] = [];
  loaded$ = new BehaviorSubject<boolean>(false);
  is_clicked = false;

  constructor(
    @Optional() @Self() public _ngControl: NgControl,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() public parentFormField: MatFormField,
    public parentform: FormGroupDirective,
  ) {
    this.ngControl = _ngControl;
    if (this._ngControl != null) {
      this._ngControl.valueAccessor = this;
    }
    this.valueChangeSubs = this.control.valueChanges
      .subscribe(async x => {
        this.is_clicked = false;
        this.filtered = await this.corefilter(x);
      });

  }

  getDisplayMember(item: any) {
    return typeof this.displayMember === 'string' ? item[this.displayMember] : this.displayMember(item);
  }
  getSecondLineMember(item: any) {
    if (!!!this.secondLineMember) {
      return null;
    }
    return typeof this.secondLineMember === 'string' ? item[this.secondLineMember] : this.secondLineMember(item);
  }
  displayMemberFn() {
    return (value: any) => {
      if (value && this.displayMember) {
        const item = this.origin?.find(x => this.valueMemberFn(x) === value);
        if (item) {
          return this.getDisplayMember(item);
        } else {
          return item;
        }
      } else {
        return undefined;
      }
    };
  }
  valueMemberFn(item: any) {
    return this.valueMember ? item[this.valueMember] : item;
  }
  optionSelected(ev: any) {
    this.is_clicked = true;
    this.value = ev.option.value;
    this.onChange(ev.option.value);
    this.optionSelectedEvent.emit([ev.option.value, this.origin]);
  }
  private async corefilter(value: string) {
    if (value && typeof value !== 'number') {
      if (!this.origin) {
        await this.downloadOrigin();
      }
      const res = value || value !== '' ? this.origin
          .filter(item => this.getDisplayMember(item)?.toLowerCase().indexOf(value?.toLowerCase()) === 0) : this.origin;
      return res.slice(0, 100) || [];
    } else {
      return [];
    }
  }
  private async downloadOrigin() {
    if (this.source) {
      this.loaded$.next(false);
      this.sourceSubs = this.source
        .subscribe(
          (x: any) => {
            this.loaded$.next(true);
            this.origin = x;
          },
          (err: any) => console.log(err)
        );
    }
  }

  //#region Inherit fn
  writeValue(obj: any) {
    this.loaded$.subscribe(res => {
      if (res) {
        this.value = obj;
        this.onTouched();
      }
    });
  }
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.stateChanges.next();
    this.statusChanges.next();
  }
  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }
  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this._elementRef?.nativeElement?.querySelector('input')?.focus();
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.statusChanges.complete();
    this.sourceSubs?.unsubscribe();
    this.valueChangeSubs?.unsubscribe();
  }
  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
      this.statusChanges.next();
    }
  }
  onFocusOut(event: FocusEvent) {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      
      this.focused = false;
      this.touched = true;
      if ((!(event.relatedTarget as any)?.localName || (event.relatedTarget as any)?.localName === 'input') && !!this.control.value) {
        this.control.setValue(this.value);
        this.onChange(this.value);
        this.onTouched();
        this.stateChanges.next();
        this.statusChanges.next();
        this._elementRef.nativeElement.dispatchEvent(new Event('blur'));
      } else if ((!(event.relatedTarget as any)?.localName || (event.relatedTarget as any)?.localName === 'input')) {
        this.value = this.control.value;
        this.onChange(this.value);
        this.onTouched();
        this.stateChanges.next();
        this.statusChanges.next();
        this._elementRef.nativeElement.dispatchEvent(new Event('blur'));
      }
    }
  }
  onTouched() {}
  onChange(value: any) {}
  //#endregion
}